import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { 
  Plus, CalendarDays, ChevronDown, ChevronRight, 
  Edit2, Trash2, X, Building2, MapPin, Users,
  DollarSign, FileText, Clock, CheckCircle, XCircle,
  AlertTriangle, Search, ExternalLink
} from 'lucide-react';

// Event statuses
const STATUSES = [
  { value: 'INQUIRY', label: 'Inquiry', color: 'bg-gray-100 text-gray-700', icon: Clock },
  { value: 'QUOTING', label: 'Quoting', color: 'bg-blue-100 text-blue-700', icon: FileText },
  { value: 'CONFIRMED', label: 'Confirmed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'bg-purple-100 text-purple-700', icon: Users },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
];

const getStatusInfo = (value) => STATUSES.find(s => s.value === value) || STATUSES[0];

function StatusBadge({ status }) {
  const info = getStatusInfo(status);
  const Icon = info.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${info.color}`}>
      <Icon className="w-3 h-3" />
      {info.label}
    </span>
  );
}

function ConfirmDeleteModal({ name, onConfirm, onClose }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-red-900">Delete Event</h2>
              <p className="text-sm text-red-700">This action cannot be undone</p>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-700">
            You are about to permanently delete <strong>{name}</strong> and all associated quotes, line items, and vendor assignments.
          </p>
          <div>
            <label className="label">Type DELETE to confirm</label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              className="input font-mono"
              placeholder="DELETE"
              autoFocus
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleConfirm}
              disabled={confirmText !== 'DELETE' || deleting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete Event'}
            </button>
            <button onClick={onClose} className="btn-secondary flex-1" disabled={deleting}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickEventModal({ onSave, onClose, clients }) {
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [guestCount, setGuestCount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/events', {
        name,
        clientId: clientId || null,
        date: date || null,
        location,
        guestCount: guestCount ? parseInt(guestCount) : null,
        notes,
        status: 'INQUIRY'
      });
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Event</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Event Name *</label>
            <input 
              value={name} 
              onChange={e => setName(e.target.value)} 
              className="input" 
              required 
              placeholder="e.g. Fall Festival 2026"
            />
          </div>
          <div>
            <label className="label">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input">
              <option value="">Select client...</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Event Date</label>
              <input 
                type="date" 
                value={date} 
                onChange={e => setDate(e.target.value)} 
                className="input"
              />
            </div>
            <div>
              <label className="label">Guest Count</label>
              <input 
                type="number" 
                value={guestCount} 
                onChange={e => setGuestCount(e.target.value)} 
                className="input"
                placeholder="500"
                min="0"
              />
            </div>
          </div>
          <div>
            <label className="label">Location</label>
            <input 
              value={location} 
              onChange={e => setLocation(e.target.value)} 
              className="input"
              placeholder="Venue or address"
            />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea 
              value={notes} 
              onChange={e => setNotes(e.target.value)} 
              className="input" 
              rows={2}
              placeholder="Initial details from client inquiry..."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>
              {saving ? 'Creating...' : 'Create Event'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatCurrency(amount) {
  if (!amount) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
}

function formatDate(dateString) {
  if (!dateString) return 'TBD';
  return new Date(dateString).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
}

export default function EventsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [activeStatus, setActiveStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const isOwner = user?.role === 'OWNER';

  const loadEvents = async () => {
    try {
      const [eventsData, clientsData] = await Promise.all([
        api.get('/events'),
        api.get('/clients')
      ]);
      setEvents(Array.isArray(eventsData) ? eventsData : eventsData?.events || []);
      setClients(Array.isArray(clientsData) ? clientsData : clientsData?.clients || []);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEvents(); }, []);

  const toggleEvent = (id) => setExpandedEvents(p => ({ ...p, [id]: !p[id] }));

  const deleteEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      setDeleteModal(null);
      loadEvents();
    } catch (err) {
      alert(err.message);
      setDeleteModal(null);
    }
  };

  const updateStatus = async (eventId, newStatus) => {
    try {
      await api.put(`/events/${eventId}`, { status: newStatus });
      loadEvents();
    } catch (err) {
      alert(err.message);
    }
  };

  // Filter events
  let filteredEvents = events;
  
  if (activeStatus !== 'ALL') {
    filteredEvents = filteredEvents.filter(e => e.status === activeStatus);
  }
  
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredEvents = filteredEvents.filter(e => 
      e.name?.toLowerCase().includes(query) ||
      e.client?.name?.toLowerCase().includes(query) ||
      e.location?.toLowerCase().includes(query)
    );
  }

  // Sort by date (upcoming first), then by created date
  filteredEvents = [...filteredEvents].sort((a, b) => {
    if (a.date && b.date) return new Date(a.date) - new Date(b.date);
    if (a.date) return -1;
    if (b.date) return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const statusCounts = STATUSES.reduce((acc, status) => {
    acc[status.value] = events.filter(e => e.status === status.value).length;
    return acc;
  }, {});

  // Calculate totals for summary
  const activeEvents = events.filter(e => ['CONFIRMED', 'IN_PROGRESS'].includes(e.status));
  const totalValue = activeEvents.reduce((sum, e) => sum + parseFloat(e.quotes?.[0]?.total || 0), 0);

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Events</h1>
          <p className="text-gray-500 text-sm mt-1">
            {activeEvents.length} active events • {formatCurrency(totalValue)} total value
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => navigate('/admin/events/brainstorm')} 
            className="btn-secondary"
          >
            <FileText className="w-4 h-4" />
            Brainstorm
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            New Event
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search events, clients, locations..."
          className="input pl-10 w-full"
        />
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-3">
        <button
          onClick={() => setActiveStatus('ALL')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeStatus === 'ALL' ? 'bg-hub-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({events.length})
        </button>
        {STATUSES.map(status => {
          const Icon = status.icon;
          const count = statusCounts[status.value] || 0;
          return (
            <button
              key={status.value}
              onClick={() => setActiveStatus(status.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeStatus === status.value ? 'bg-hub-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className={`w-4 h-4 ${activeStatus === status.value ? 'text-white' : ''}`} />
              <span className="hidden sm:inline">{status.label}</span>
              ({count})
            </button>
          );
        })}
      </div>

      {/* Events List */}
      <div className="space-y-3">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <CalendarDays className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No events found</p>
            {searchQuery && <p className="text-sm mt-1">Try adjusting your search</p>}
          </div>
        ) : (
          filteredEvents.map(event => {
            const isExpanded = expandedEvents[event.id];
            const quote = event.quotes?.[0]; // Most recent quote
            const totalPrice = parseFloat(quote?.total || 0);
            const totalCost = parseFloat(quote?.totalCost || 0);
            const margin = totalPrice > 0 ? ((totalPrice - totalCost) / totalPrice * 100).toFixed(1) : 0;
            
            return (
              <div key={event.id} className="card overflow-hidden">
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleEvent(event.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-hub-100 rounded-lg flex items-center justify-center">
                      <CalendarDays className="w-5 h-5 text-hub-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{event.name}</span>
                        <StatusBadge status={event.status} />
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        {event.client && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {event.client.name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          {formatDate(event.date)}
                        </span>
                        {event.guestCount && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {event.guestCount} guests
                          </span>
                        )}
                        {totalPrice > 0 && (
                          <span className="flex items-center gap-1 text-green-600 font-medium">
                            <DollarSign className="w-3 h-3" />
                            {formatCurrency(totalPrice)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/events/builder/${event.id}`); }}
                      className="p-2 text-gray-400 hover:text-hub-600 hover:bg-hub-50 rounded-lg"
                      title="Open in Event Builder"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setDeleteModal(event); }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4 space-y-4">
                    {/* Event Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {event.location && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Location</p>
                            <p className="text-sm font-medium">{event.location}</p>
                          </div>
                        </div>
                      )}
                      {event.guestCount && (
                        <div className="flex items-start gap-2">
                          <Users className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Guests</p>
                            <p className="text-sm font-medium">{event.guestCount}</p>
                          </div>
                        </div>
                      )}
                      {totalPrice > 0 && (
                        <div className="flex items-start gap-2">
                          <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Quote Value</p>
                            <p className="text-sm font-medium">{formatCurrency(totalPrice)}</p>
                          </div>
                        </div>
                      )}
                      {totalPrice > 0 && (
                        <div className="flex items-start gap-2">
                          <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Margin</p>
                            <p className={`text-sm font-medium ${margin >= 25 ? 'text-green-600' : margin >= 15 ? 'text-amber-600' : 'text-red-600'}`}>
                              {margin}% ({formatCurrency(totalPrice - totalCost)})
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {event.notes && (
                      <div className="text-sm text-gray-600 bg-white p-3 rounded-lg border">
                        <p className="text-xs text-gray-400 mb-1">Notes</p>
                        {event.notes}
                      </div>
                    )}

                    {/* Status Change */}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <span className="text-sm text-gray-500">Change status:</span>
                      <div className="flex flex-wrap gap-1">
                        {STATUSES.map(status => (
                          <button
                            key={status.value}
                            onClick={() => updateStatus(event.id, status.value)}
                            disabled={event.status === status.value}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                              event.status === status.value 
                                ? status.color + ' cursor-default' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {status.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => navigate(`/admin/events/builder/${event.id}`)}
                        className="btn-primary text-sm"
                      >
                        <Edit2 className="w-4 h-4" />
                        Open Event Builder
                      </button>
                      {quote && quote.quoteNumber && (
                        <span className="text-sm text-gray-500">
                          {quote.quoteNumber}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <QuickEventModal 
          clients={clients}
          onSave={() => { setShowModal(false); loadEvents(); }} 
          onClose={() => setShowModal(false)} 
        />
      )}
      
      {deleteModal && (
        <ConfirmDeleteModal
          name={deleteModal.name}
          onConfirm={() => deleteEvent(deleteModal.id)}
          onClose={() => setDeleteModal(null)}
        />
      )}
    </div>
  );
}
